// Classifies and navigates a notification's action URL.
//
// Action URLs are generated server-side and may be absolute
// (https://bosko.app/pl/app/biblia/... — localized deep-links always are) or
// relative (/app/activity). Most point at our own routes and should open
// in-place via Inertia, but a notification may one day deep-link somewhere
// external (a Stripe billing portal, a help article), which must open outside
// the app. Anything with an unsafe scheme (javascript:, data:) is ignored.

// Opens an external URL. On native this must leave the app's webview — a plain
// window.open would load the page inside the Capacitor shell — so route it
// through the Capacitor Browser (in-app SFSafariViewController / Custom Tab),
// matching how billing/OAuth links open. On web, a new tab with noopener.
export async function openExternal(href) {
  // Imported lazily so the pure classify/navigate helpers stay decoupled from
  // the Capacitor/Inertia module chain (and cheap to unit-test).
  const { isNative } = await import('@/lib/capacitor');
  if (isNative()) {
    try {
      const { Browser } = await import('@capacitor/browser');
      await Browser.open({ url: href });
      return;
    } catch {
      // Plugin unavailable — fall through to a normal new-tab open.
    }
  }
  window.open(href, '_blank', 'noopener,noreferrer');
}

// Returns one of:
//   { kind: 'internal', path }  — same-origin: path+search+hash for router.visit
//   { kind: 'external', href }  — cross-origin http(s): open in a new tab
//   { kind: 'none' }            — blank, malformed, or unsafe scheme: do nothing
// Returning path+search+hash (not the absolute URL) keeps Inertia treating it as
// an internal visit and preserves #verse-<id> style deep-link anchors.
export function classifyActionUrl(actionUrl) {
  if (!actionUrl || typeof actionUrl !== 'string') return { kind: 'none' };

  let url;
  try {
    url = new URL(actionUrl, window.location.origin);
  } catch {
    return { kind: 'none' };
  }

  if (url.origin === window.location.origin) {
    return {
      kind: 'internal',
      path: `${url.pathname}${url.search}${url.hash}`,
    };
  }
  if (url.protocol === 'http:' || url.protocol === 'https:') {
    return { kind: 'external', href: url.href };
  }
  return { kind: 'none' };
}

// Navigates to a notification action URL: same-origin via Inertia's router,
// cross-origin in a new tab. `router` and `open` are injected so this stays
// unit-testable. Returns the resolved kind ('internal' | 'external' | 'none').
export function navigateToActionUrl(
  actionUrl,
  { router, open = openExternal },
) {
  const target = classifyActionUrl(actionUrl);
  if (target.kind === 'internal') router.visit(target.path);
  else if (target.kind === 'external') open(target.href);
  return target.kind;
}
